import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles } from "lucide-react";
import type { AppSettings } from "@shared/schema";

/**
 * Admin > Experience: app-wide rollout controls for the new experience.
 * Currently one switch: disable the classic view for all users.
 */
export default function ExperienceManager() {
  const { toast } = useToast();

  const { data: settings, isLoading } = useQuery<AppSettings>({
    queryKey: ["/api/app-settings"],
  });

  const save = useMutation({
    mutationFn: async (disableClassicView: boolean) =>
      apiRequest("/api/app-settings", "PUT", { disableClassicView }),
    onSuccess: (_data, disableClassicView) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app-settings"] });
      toast({
        title: disableClassicView ? "Classic view disabled" : "Classic view restored",
        description: disableClassicView
          ? "All users now land in the Sponsor Workbench."
          : "Users can switch between the classic view and the new experience again.",
      });
    },
    onError: (error: Error) => {
      toast({ title: "Save failed", description: error.message || "Could not save the setting", variant: "destructive" });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          Experience Settings
        </CardTitle>
        <CardDescription>
          Control the rollout of the new experience (Sponsor Workbench) across the whole app.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <div className="flex items-start justify-between gap-6 rounded-lg border border-gray-200 p-4">
            <div className="space-y-1">
              <p className="font-medium">Disable classic view for all users</p>
              <p className="text-sm text-gray-500">
                When on: everyone lands in the Sponsor Workbench, the classic dashboard and
                station tool pages redirect there, and the header experience toggle is hidden.
                Admin settings (this page) stay reachable, so you can switch back anytime.
              </p>
            </div>
            <Switch
              checked={settings?.disableClassicView ?? false}
              disabled={save.isPending}
              onCheckedChange={(value) => save.mutate(value)}
              data-testid="switch-disable-classic-view"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
